/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignStatus, StudioCampaign } from "./types";
import { STUDIO_CHANNELS } from "./types";
import { deleteStudioCampaign, newId, saveStudioCampaign, STUB_SEGMENTS, studioStore } from "./stub";
import { CHANNEL_META, formatMoney } from "./shared";
import { CampaignBuilder } from "./CampaignBuilder";
import { CampaignPerformance } from "./CampaignPerformance";

const STATUS_BADGE: Record<CampaignStatus, string> = {
  draft: "badge-ghost",
  scheduled: "badge-info",
  sending: "badge-warning",
  sent: "badge-success",
  paused: "badge-neutral",
};

const FILTERS = ["all", "draft", "scheduled", "sending", "sent"] as const;

// Campaign studio: multi-channel campaigns (email, WhatsApp, SMS, wallet push).
// Frontend-only for now — all data comes from the stub store in ./stub.ts.
export const LoyaltyCampaignStudioPage = defineComponent({
  name: "LoyaltyCampaignStudioPage",
  setup() {
    const { t } = useLocalization();
    const filter = ref<(typeof FILTERS)[number]>("all");
    const expanded = ref<string | null>(null);
    // undefined = closed, null = new campaign, string = editing that id.
    const editing = ref<string | null | undefined>(undefined);

    const campaigns = computed(() =>
      studioStore.campaigns.filter((c) => filter.value === "all" || c.status === filter.value),
    );

    const totals = computed(() =>
      studioStore.campaigns.reduce(
        (acc, c) => {
          if (!c.stats) return acc;
          acc.sent += c.stats.sent;
          acc.delivered += c.stats.delivered;
          acc.redeemed += c.stats.redeemed;
          acc.revenue += c.stats.revenue;
          return acc;
        },
        { sent: 0, delivered: 0, redeemed: 0, revenue: 0 },
      ),
    );

    const duplicate = (c: StudioCampaign) => {
      saveStudioCampaign({
        ...JSON.parse(JSON.stringify(c)),
        id: newId(),
        name: t("campaigns.studio_list.copy_of", [c.name]),
        status: "draft",
        stats: null,
      });
    };

    const remove = (c: StudioCampaign) => {
      if (window.confirm(t("campaigns.studio_list.confirm_delete", [c.name]))) deleteStudioCampaign(c.id);
    };

    const Kpi = (icon: string, label: string, value: string) => (
      <div class="flex items-center gap-3 rounded-2xl border border-base-300 bg-base-100 p-4">
        <span class="material-symbols-outlined rounded-xl bg-primary/10 p-2 text-primary">{icon}</span>
        <div>
          <div class="text-xs text-base-content/60">{label}</div>
          <div class="text-xl font-bold tabular-nums">{value}</div>
        </div>
      </div>
    );

    const Row = (c: StudioCampaign) => {
      const segment = STUB_SEGMENTS.find((s) => s.id === c.segment_id);
      const open = expanded.value === c.id;
      return [
        <tr key={c.id} class="hover cursor-pointer" onClick={() => (expanded.value = open ? null : c.id)}>
          <td>
            <div class="flex items-center gap-2">
              <span class={["material-symbols-outlined text-base text-base-content/40 transition", open && "rotate-90"]}>
                {c.stats ? "chevron_right" : ""}
              </span>
              <div>
                <div class="font-semibold">{c.name || t("campaigns.studio.untitled")}</div>
                <div class="text-xs text-base-content/50">{segment?.name}</div>
              </div>
            </div>
          </td>
          <td>
            <span class={["badge badge-sm", STATUS_BADGE[c.status]]}>{t(`campaigns.studio.status_${c.status}`)}</span>
          </td>
          <td>
            <div class="flex gap-1">
              {STUDIO_CHANNELS.filter((ch) => c.channels.includes(ch)).map((ch) => (
                <span key={ch} title={t(CHANNEL_META[ch].label)} class={["material-symbols-outlined text-lg", CHANNEL_META[ch].tone]}>
                  {CHANNEL_META[ch].icon}
                </span>
              ))}
            </div>
          </td>
          <td class="text-right tabular-nums">{c.stats ? c.stats.sent.toLocaleString("de-CH") : "—"}</td>
          <td class="text-right tabular-nums">{c.stats ? c.stats.redeemed.toLocaleString("de-CH") : "—"}</td>
          <td class="text-right tabular-nums">{c.stats ? formatMoney(c.stats.revenue) : "—"}</td>
          <td class="text-xs text-base-content/60">{new Date(c.updated_at).toLocaleDateString("de-CH")}</td>
          <td onClick={(e: MouseEvent) => e.stopPropagation()}>
            <div class="flex justify-end gap-1">
              <button type="button" class="btn btn-ghost btn-xs btn-square" title={t("campaigns.studio_list.edit")} onClick={() => (editing.value = c.id)}>
                <span class="material-symbols-outlined text-base">edit</span>
              </button>
              <button type="button" class="btn btn-ghost btn-xs btn-square" title={t("campaigns.studio_list.duplicate")} onClick={() => duplicate(c)}>
                <span class="material-symbols-outlined text-base">content_copy</span>
              </button>
              <button type="button" class="btn btn-ghost btn-xs btn-square" title={t("campaigns.studio_list.delete")} onClick={() => remove(c)}>
                <span class="material-symbols-outlined text-base">delete</span>
              </button>
            </div>
          </td>
        </tr>,
        open && c.stats ? (
          <tr key={`${c.id}-stats`}>
            <td colspan={8} class="p-0">
              <CampaignPerformance stats={c.stats} />
            </td>
          </tr>
        ) : null,
      ];
    };

    return () => (
      <div class="space-y-6 p-4 md:p-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 class="flex items-center gap-2 text-2xl font-bold">
              {t("campaigns.studio_list.title")}
              <span class="badge badge-warning badge-sm">{t("campaigns.studio_list.preview_badge")}</span>
            </h2>
            <p class="text-sm text-base-content/60">{t("campaigns.studio_list.subtitle")}</p>
          </div>
          <button type="button" class="btn btn-primary gap-2" onClick={() => (editing.value = null)}>
            <span class="material-symbols-outlined">add</span>
            {t("campaigns.studio_list.new")}
          </button>
        </div>

        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Kpi("send", t("campaigns.studio_list.kpi_sent"), totals.value.sent.toLocaleString("de-CH"))}
          {Kpi(
            "mark_email_read",
            t("campaigns.studio_list.kpi_delivery"),
            totals.value.sent ? `${Math.round((totals.value.delivered / totals.value.sent) * 100)}%` : "—",
          )}
          {Kpi("confirmation_number", t("campaigns.studio_list.kpi_redeemed"), totals.value.redeemed.toLocaleString("de-CH"))}
          {Kpi("payments", t("campaigns.studio_list.kpi_revenue"), formatMoney(totals.value.revenue))}
        </div>

        <div class="rounded-2xl border border-base-300 bg-base-100">
          <div role="tablist" class="tabs tabs-bordered px-2 pt-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                type="button"
                class={["tab", filter.value === f && "tab-active"]}
                onClick={() => (filter.value = f)}
              >
                {t(f === "all" ? "campaigns.studio_list.filter_all" : `campaigns.studio.status_${f}`)}
              </button>
            ))}
          </div>
          <div class="overflow-x-auto">
            <table class="table">
              <thead>
                <tr>
                  <th>{t("campaigns.studio_list.campaign")}</th>
                  <th>{t("campaigns.studio_list.status")}</th>
                  <th>{t("campaigns.studio_list.channels")}</th>
                  <th class="text-right">{t("campaigns.studio_list.sent")}</th>
                  <th class="text-right">{t("campaigns.studio_list.redeemed")}</th>
                  <th class="text-right">{t("campaigns.studio_list.revenue")}</th>
                  <th>{t("campaigns.studio_list.updated")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>{campaigns.value.flatMap(Row)}</tbody>
            </table>
            {campaigns.value.length === 0 && (
              <div class="flex flex-col items-center gap-2 p-10 text-base-content/50">
                <span class="material-symbols-outlined text-4xl">campaign</span>
                {t("campaigns.studio_list.empty")}
              </div>
            )}
          </div>
        </div>

        {editing.value !== undefined && (
          <CampaignBuilder key={editing.value ?? "new"} campaignId={editing.value} onClose={() => (editing.value = undefined)} />
        )}
      </div>
    );
  },
});
